import {
  isEndpointAllowed,
  isOriginTrusted,
} from '@de/config/allowed-data-sources';
import {
  DataSourceHttpError,
  DataSourceEndpointNotAllowedError,
  DataSourceReportingNotConfiguredError,
  DataSourceTransportError,
  type ReportingModelDataSourceDeclaration,
} from '@de/providers/DataSourceManager.types';
import { extractResponsePath } from './extractResponsePath';
import { interpolateReportingBody } from './interpolateReportingBody';
import {
  BASE_BACKOFF_MS,
  DEFAULT_RETRIES,
  DEFAULT_TIMEOUT_MS,
  type FetchDataSourceOptions,
  type FetchDataSourceResult,
  type InterpolatedRequest,
} from './fetchDataSource';
import { isReportingError } from './reporting-query-types';

/**
 * The code used when a failure never got far enough to name itself -- a gateway
 * 502 with an HTML body, anything whose body is not the service's error
 * envelope, and a request that got no response at all.
 *
 * Defaulting rather than leaving it unset is what lets a host recognize an
 * infrastructure failure and substitute its own copy. The alternative is
 * showing whoever is looking at the screen the raw body of a 502, or the
 * endpoint and method of a request that timed out.
 */
const UNRECOGNIZED_ERROR_CODE = 'SERVICE_UNAVAILABLE';

export interface FetchReportingModelOptions extends FetchDataSourceOptions {
  readonly reportingQueryUrl?: string;
}

/**
 * Shape a reporting-model declaration into the request this module will send.
 *
 * Exported so the cache key derives from the same shaping rather than from a
 * parallel copy. The headers matter to that: the copy this replaced omitted
 * `content-type`, which was harmless only because the value is constant.
 */
export function buildReportingModelRequest(
  decl: ReportingModelDataSourceDeclaration,
  interpolationContext: unknown,
  reportingQueryUrl: string | undefined,
): InterpolatedRequest {
  return {
    endpoint: reportingQueryUrl?.trim() ?? '',
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: interpolateReportingBody(decl, interpolationContext),
  };
}

/**
 * Reporting-model fetch path. The retry/backoff loop intentionally mirrors
 * fetchDataSource rather than sharing a helper — the two paths stay
 * independently auditable (different request shaping, error parsing, and
 * allowlist semantics). Do not DRY them without revisiting both call sites.
 */
export async function fetchReportingModelDataSource(
  decl: ReportingModelDataSourceDeclaration,
  interpolationContext: unknown,
  options: FetchReportingModelOptions = {},
): Promise<FetchDataSourceResult> {
  const reportingQueryUrl = options.reportingQueryUrl?.trim();
  if (!reportingQueryUrl) {
    return {
      status: 'error',
      request: { endpoint: '', method: 'POST' },
      error: new DataSourceReportingNotConfiguredError(),
    };
  }

  const built = options.preparedRequest
    ? options.preparedRequest
    : buildReportingModelRequest(decl, interpolationContext, reportingQueryUrl);
  const request =
    options.preparedRequest || !options.coerceRequestBody
      ? built
      : options.coerceRequestBody(built);

  if (!isEndpointAllowed(request.endpoint, request.method, options.tenantId)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[DataSourceManager] blocked reporting endpoint by allowlist: ${request.method} ${request.endpoint}`,
    );
    return {
      status: 'error',
      request,
      error: new DataSourceEndpointNotAllowedError(
        request.endpoint,
        request.method,
      ),
    };
  }

  if (!isOriginTrusted(request.endpoint, options.trustedOrigins)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[DataSourceManager] blocked reporting endpoint by origin allowlist: ${request.method} ${request.endpoint}`,
    );
    return {
      status: 'error',
      request,
      error: new DataSourceEndpointNotAllowedError(
        request.endpoint,
        request.method,
      ),
    };
  }

  const timeout = decl.errorStrategy?.timeout ?? DEFAULT_TIMEOUT_MS;
  const retries = decl.errorStrategy?.retries ?? DEFAULT_RETRIES;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const delay = options.delay ?? defaultDelay;

  let lastError: Error = new Error(
    'ReportingModel fetch never attempted — unreachable.',
  );

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (options.signal?.aborted) {
      return {
        status: 'error',
        request,
        error: toError(options.signal.reason, 'ReportingModel fetch aborted.'),
      };
    }

    // Failing to reach the service and failing to read what it sent back land
    // in the same catch, and only the first is an outage. Extraction throws
    // DataSourceExtractionError from here, so coding the catch wholesale would
    // both relabel a response-shape problem as downtime and replace a typed
    // error a host may be matching on.
    let responded = false;

    try {
      const response = await fetchOnce(
        request,
        fetchImpl,
        timeout,
        options.signal,
        options.setTimeoutImpl,
        options.clearTimeoutImpl,
      );
      responded = true;

      if (response.status >= 400 && response.status < 500) {
        return {
          status: 'error',
          request,
          error: await toReportingFetchError(response, request),
        };
      }
      if (response.status >= 500) {
        // Coded so a host can recognize this as infrastructure and say so in
        // its own words. Left uncoded, the fallback message a host renders
        // names the endpoint and method, which is a detail for an operator and
        // not for whoever is looking at the screen.
        lastError = new DataSourceHttpError(
          response.status,
          request.endpoint,
          request.method,
          undefined,
          UNRECOGNIZED_ERROR_CODE,
        );
        if (attempt < retries) {
          await delay(backoffMs(attempt), options.signal);
          continue;
        }
        return { status: 'error', request, error: lastError };
      }

      const json = (await response.json()) as unknown;
      const coerced = options.coerceResponse
        ? options.coerceResponse(json, request)
        : json;
      const extracted = extractResponsePath(coerced, decl.responsePath);

      if (isEmpty(extracted)) {
        return { status: 'empty', request, data: extracted };
      }
      return { status: 'resolved', request, data: extracted };
    } catch (error) {
      const failure = toError(error, 'ReportingModel fetch failed.');
      // Only the caller's signal makes this a cancellation, which is the host's
      // own decision -- a navigation away, or a newer request superseding this
      // one -- and so stays uncoded rather than announcing that reporting is
      // down for something the host asked to stop.
      //
      // Deliberately not keyed off the thrown value's name. fetchOnce aborts
      // its own controller for the timeout, and what that surfaces as depends
      // on the runtime: one that propagates an abort reason rethrows the
      // timeout Error, while an older one discards the reason and raises a bare
      // AbortError. Asking who aborted instead of what was thrown keeps a
      // timeout a timeout on both, so it is retried and coded either way.
      if (options.signal?.aborted) {
        lastError = failure;
        return { status: 'error', request, error: lastError };
      }
      lastError = responded
        ? failure
        : new DataSourceTransportError(
            request.endpoint,
            request.method,
            undefined,
            UNRECOGNIZED_ERROR_CODE,
          );
      if (attempt < retries) {
        try {
          await delay(backoffMs(attempt), options.signal);
        } catch {
          return { status: 'error', request, error: lastError };
        }
        continue;
      }
      return { status: 'error', request, error: lastError };
    }
  }

  return { status: 'error', request, error: lastError };
}

async function toReportingFetchError(
  response: Response,
  request: InterpolatedRequest,
): Promise<Error> {
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return new DataSourceHttpError(
      response.status,
      request.endpoint,
      request.method,
      text || response.statusText,
      UNRECOGNIZED_ERROR_CODE,
    );
  }
  if (isReportingError(parsed)) {
    const { code, message } = parsed.error;
    return new DataSourceHttpError(
      response.status,
      request.endpoint,
      request.method,
      message,
      code,
    );
  }
  // JSON, but not the service's envelope -- as unrecognizable as a 502's HTML.
  return new DataSourceHttpError(
    response.status,
    request.endpoint,
    request.method,
    text || response.statusText,
    UNRECOGNIZED_ERROR_CODE,
  );
}

async function fetchOnce(
  request: InterpolatedRequest,
  fetchImpl: typeof fetch,
  timeout: number,
  outerSignal: AbortSignal | undefined,
  setTimeoutImpl: typeof setTimeout = setTimeout,
  clearTimeoutImpl: typeof clearTimeout = clearTimeout,
): Promise<Response> {
  const controller = new AbortController();
  const onOuterAbort = (): void => {
    controller.abort(outerSignal?.reason);
  };
  if (outerSignal) {
    if (outerSignal.aborted) {
      controller.abort(outerSignal.reason);
    } else {
      outerSignal.addEventListener('abort', onOuterAbort, { once: true });
    }
  }
  const timer = setTimeoutImpl(() => {
    controller.abort(new Error('ReportingModel fetch timed out.'));
  }, timeout);

  try {
    return await fetchImpl(request.endpoint, {
      method: request.method,
      headers: request.headers,
      body: JSON.stringify(request.body),
      signal: controller.signal,
    });
  } finally {
    clearTimeoutImpl(timer);
    if (outerSignal) {
      outerSignal.removeEventListener('abort', onOuterAbort);
    }
  }
}

function backoffMs(attempt: number): number {
  return BASE_BACKOFF_MS * 2 ** attempt;
}

function defaultDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(
        toError(signal.reason, 'ReportingModel fetch aborted during backoff.'),
      );
      return;
    }
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      cleanup();
      reject(
        toError(signal?.reason, 'ReportingModel fetch aborted during backoff.'),
      );
    };
    const cleanup = (): void => {
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }
  return false;
}

function toError(value: unknown, fallback: string): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'string') {
    return new Error(value);
  }
  return new Error(fallback);
}
